"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { Mic, MicOff, Search } from "lucide-react";
import { Input } from "@/shared/ui/inputs/input";
import { Button } from "@/shared/ui/buttons/button";
import { RoleIcon } from "@/shared/ui/data-display/role-icon";
import { getRiotRankImageUrl } from "@/shared/lib/rank-image";
import {
  HOUR_SLOT_COUNT,
  LFG_FORMATS,
  LFG_LANGUAGES,
  LFG_MIC,
  LFG_RANKS,
  LFG_ROLES,
  LFG_SERVERS,
  toggleIn,
  toggleRank,
  withFilters,
  type BoardFilters,
  type LfgRole,
} from "../model/board-filters";
import { BoardChip } from "./board-chip";
import { BoardFilterGroup } from "./board-filter-group";

const GROUPS = ["role", "rank", "mic", "hours", "format", "server", "language"] as const;
type Group = (typeof GROUPS)[number];
/** Как в макете: роли и микро открыты, длинные списки свёрнуты. */
const DEFAULT_OPEN: Record<Group, boolean> = { role: true, rank: false, mic: true, hours: false, format: false, server: false, language: false };

/** Ключ RoleIcon для роли бэкенда: у «Автофилла» иконка позиции fill. */
const ROLE_ICON_KEY: Record<LfgRole, string> = {
  Top: "top", Jungle: "jungle", Mid: "mid", Adc: "adc", Support: "support",
  Autofill: "fill", Substitute: "substitute", Coach: "coach",
};

const SEARCH_DEBOUNCE_MS = 300;

interface BoardFiltersPanelProps {
  filters: BoardFilters;
  onChange: (next: BoardFilters) => void;
  /** Подпись пояса для часов: «МСК», «UTC+5». */
  tzLabel: string;
}

export function BoardFiltersPanel({ filters, onChange, tzLabel }: BoardFiltersPanelProps) {
  const t = useTranslations("LfgBoard");
  const [open, setOpen] = useState(DEFAULT_OPEN);
  const [search, setSearch] = useState(filters.q);
  // Поиск сменился снаружи («Сбросить всё», «назад») — поле догоняет адрес, иначе
  // отложенная запись ниже вернула бы старый текст обратно.
  const [syncedQ, setSyncedQ] = useState(filters.q);
  const [sentQ, setSentQ] = useState<string | null>(null);
  if (filters.q !== syncedQ) {
    setSyncedQ(filters.q);
    // Вернулось то, что отправили мы сами, — поле не трогаем: человек мог допечатать.
    if (filters.q !== sentQ) setSearch(filters.q);
  }
  const allOpen = GROUPS.every((g) => open[g]);

  // Поиск уходит в адрес с задержкой: иначе каждая буква — запрос к API и запись в историю.
  useEffect(() => {
    if (search.trim() === filters.q) return;
    const id = setTimeout(() => {
      setSentQ(search.trim());
      onChange(withFilters(filters, { q: search.trim() }));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [search, filters, onChange]);

  const group = (g: Group) => ({
    open: open[g],
    onOpenChange: (v: boolean) => setOpen((o) => ({ ...o, [g]: v })),
  });

  return (
    <div>
      <div className="relative mb-4">
        <Search aria-hidden className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("searchPlaceholder")}
          aria-label={t("searchLabel")}
          className="h-9 pl-9 text-sm"
        />
      </div>

      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{t("filtersTitle")}</span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-6 rounded-full px-2.5 text-[11px]"
          onClick={() => setOpen(Object.fromEntries(GROUPS.map((g) => [g, !allOpen])) as Record<Group, boolean>)}
        >
          {allOpen ? t("collapseAll") : t("expandAll")}
        </Button>
      </div>

      <BoardFilterGroup title={t("groupRole")} count={filters.roles.length} gridClassName="grid-cols-2" {...group("role")}>
        {LFG_ROLES.map((r) => (
          <BoardChip
            key={r}
            pressed={filters.roles.includes(r)}
            onPressedChange={() => onChange(withFilters(filters, { roles: toggleIn(filters.roles, r, LFG_ROLES) }))}
            icon={<RoleIcon role={ROLE_ICON_KEY[r]} size={15} className="text-current" />}
          >
            {t(`role.${r}`)}
          </BoardChip>
        ))}
      </BoardFilterGroup>

      <BoardFilterGroup title={t("groupRank")} count={filters.rank ? 1 : 0} gridClassName="grid-cols-2" {...group("rank")}>
        {LFG_RANKS.map((r, tier) => {
          const emblem = getRiotRankImageUrl(r);
          return (
            <BoardChip
              key={r}
              pressed={!!filters.rank && tier >= filters.rank.min && tier <= filters.rank.max}
              onPressedChange={() => onChange(withFilters(filters, { rank: toggleRank(filters.rank, tier) }))}
              icon={emblem ? <Image src={emblem} alt="" width={16} height={16} className="size-4" /> : undefined}
            >
              {t(`rank.${r}`)}
            </BoardChip>
          );
        })}
      </BoardFilterGroup>

      <BoardFilterGroup title={t("groupMic")} count={filters.mic === "any" ? 0 : 1} gridClassName="grid-cols-1" {...group("mic")}>
        {LFG_MIC.map((m) => (
          <BoardChip
            key={m}
            pressed={filters.mic === m}
            // Выбор одного значения: повторный клик по выбранному возвращает «не важно».
            onPressedChange={() => onChange(withFilters(filters, { mic: filters.mic === m ? "any" : m }))}
            icon={
              m === "with" ? <Mic aria-hidden className="size-3.5 text-score-win" /> :
              m === "without" ? <MicOff aria-hidden className="size-3.5 text-destructive" /> : undefined
            }
          >
            {t(`mic.${m}`)}
          </BoardChip>
        ))}
      </BoardFilterGroup>

      <BoardFilterGroup title={t("groupHours", { tz: tzLabel })} count={filters.hourSlots.length} gridClassName="grid-cols-2" {...group("hours")}>
        {[...Array(HOUR_SLOT_COUNT).keys()].map((slot) => (
          <BoardChip
            key={slot}
            pressed={filters.hourSlots.includes(slot)}
            onPressedChange={() => onChange(withFilters(filters, { hourSlots: toggleIn(filters.hourSlots, slot, [...Array(HOUR_SLOT_COUNT).keys()]) }))}
          >
            {t("hoursRange", { from: String(slot * 3).padStart(2, "0"), to: String(slot * 3 + 3).padStart(2, "0") })}
          </BoardChip>
        ))}
      </BoardFilterGroup>

      <BoardFilterGroup title={t("groupFormat")} count={filters.formats.length} gridClassName="grid-cols-2" {...group("format")}>
        {LFG_FORMATS.map((f) => (
          <BoardChip
            key={f}
            pressed={filters.formats.includes(f)}
            onPressedChange={() => onChange(withFilters(filters, { formats: toggleIn(filters.formats, f, LFG_FORMATS) }))}
          >
            {t(`format.${f}`)}
          </BoardChip>
        ))}
      </BoardFilterGroup>

      <BoardFilterGroup title={t("groupServer")} count={filters.servers.length} gridClassName="grid-cols-3" {...group("server")}>
        {LFG_SERVERS.map((s) => (
          <BoardChip
            key={s}
            pressed={filters.servers.includes(s)}
            onPressedChange={() => onChange(withFilters(filters, { servers: toggleIn(filters.servers, s, LFG_SERVERS) }))}
          >
            {s}
          </BoardChip>
        ))}
      </BoardFilterGroup>

      <BoardFilterGroup title={t("groupLanguage")} count={filters.languages.length} gridClassName="grid-cols-2" {...group("language")}>
        {LFG_LANGUAGES.map((l) => (
          <BoardChip
            key={l}
            pressed={filters.languages.includes(l)}
            onPressedChange={() => onChange(withFilters(filters, { languages: toggleIn(filters.languages, l, LFG_LANGUAGES) }))}
          >
            {t(`language.${l}`)}
          </BoardChip>
        ))}
      </BoardFilterGroup>
    </div>
  );
}
